import type {Content} from '@prismicio/client'
import {isFilled} from '@prismicio/client'
import cn from 'classnames'
import {motion, useScroll, useSpring, useTransform} from 'framer-motion'
import type {LinkResolverFn} from 'prismicio'
import type {ButtonDocument} from 'prismicio-types'
import {useEffect, useRef, useState} from 'react'
import {STRESSTIMONIALS_MOBILE_ANIMATION_STYLES} from 'src/common/constants/stresstimonialsAnimations'
import {Container} from 'src/common/Container'
import {SingleCard} from './SingleCard'

type TwoCardsStresstimonialsCardProps = {
  slice: Extract<Content.StresstimonialsCardSlice, {variation: 'twoCards'}>
  pageUid: string
  componentIndex: number
  linkResolver: LinkResolverFn
  buttonsData?: ButtonDocument[]
}

export const TwoCardsStresstimonialsCard = ({
  slice,
  pageUid,
  componentIndex,
  linkResolver,
  buttonsData,
}: TwoCardsStresstimonialsCardProps) => {
  const [activeDrawerIndex, setActiveDrawerIndex] = useState<number | null>(null)
  const [isMobile, setIsMobile] = useState(false)
  const [isLeftCardVisible, setIsLeftCardVisible] = useState(false)
  const [isRightCardVisible, setIsRightCardVisible] = useState(false)
  const leftCardRef = useRef<HTMLDivElement>(null)
  const rightCardRef = useRef<HTMLDivElement>(null)

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Intersection Observer for left card
  useEffect(() => {
    if (!isMobile || !leftCardRef.current) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsLeftCardVisible(entry.isIntersecting)
        })
      },
      {
        threshold: 0.2,
        rootMargin: '0px',
      },
    )

    observer.observe(leftCardRef.current)

    return () => observer.disconnect()
  }, [isMobile])

  // Intersection Observer for right card
  useEffect(() => {
    if (!isMobile || !rightCardRef.current) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsRightCardVisible(entry.isIntersecting)
        })
      },
      {
        threshold: 0.2,
        rootMargin: '0px',
      },
    )

    observer.observe(rightCardRef.current)

    return () => observer.disconnect()
  }, [isMobile])

  // Track scroll progress through the component (desktop only)
  const {scrollYProgress} = useScroll({
    target: leftCardRef,
    offset: ['start 95%', 'start 70%'],
  })

  // Animation distance based on viewport
  const animationDistance = isMobile ? 50 : 100

  // Left card animation: from left to center
  const rawLeftCardX = useTransform(scrollYProgress, [0, 1], [-animationDistance, 0])

  const leftCardX = useSpring(rawLeftCardX, {
    stiffness: 150,
    damping: 25,
    mass: 0.5,
  })

  // Right card animation: from right to center
  const rawRightCardX = useTransform(scrollYProgress, [0, 1], [animationDistance, 0])

  const rightCardX = useSpring(rawRightCardX, {
    stiffness: 150,
    damping: 25,
    mass: 0.5,
  })

  const cards = slice.primary.cards_with_drawers || []
  const leftCard = cards[0]
  const rightCard = cards[1]

  const LeftCardWrapper = motion.div
  const RightCardWrapper = motion.div

  return (
    <>
      <style>{STRESSTIMONIALS_MOBILE_ANIMATION_STYLES}</style>
      <Container
        backgroundColor="Transparent"
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="!py-[30px] md:!py-[90px]"
      >
        <div className="mx-auto w-full max-w-[1280px] px-4 md:px-5">
          <div className="flex flex-col items-start gap-15 lg:flex-row lg:justify-between lg:gap-20">
            {/* Left Card - animates from left on desktop, bottom-up on mobile */}
            {leftCard && (
              <div ref={leftCardRef} className="flex w-full justify-start lg:w-1/2">
                <LeftCardWrapper
                  className={cn('flex w-full', {
                    'mobile-card-animated': isMobile,
                    'mobile-card-visible': isMobile && isLeftCardVisible,
                    '!justify-end': isMobile && rightCard && isFilled.image(rightCard.additional_image),
                  })}
                  {...(!isMobile && {style: {x: leftCardX}})}
                >
                  <SingleCard
                    card={leftCard}
                    cardIndex={0}
                    isActive={activeDrawerIndex === 0}
                    onOpen={() => setActiveDrawerIndex(0)}
                    onClose={() => setActiveDrawerIndex(null)}
                    bioInfo={slice.primary.drawer_bio_info_left_card}
                    linkResolver={linkResolver}
                    componentIndex={componentIndex}
                    pageUid={pageUid}
                    buttonsData={buttonsData}
                    componentName="stresstimonials-card-left"
                  />
                </LeftCardWrapper>
              </div>
            )}

            {/* Right Card - animates from right on desktop, bottom-up on mobile */}
            {rightCard && (
              <div ref={rightCardRef} className="flex w-full justify-end lg:w-1/2">
                <RightCardWrapper
                  className={cn('flex w-full justify-end', {
                    'mobile-card-animated': isMobile,
                    'mobile-card-visible': isMobile && isRightCardVisible,
                    '!justify-start': isMobile && leftCard && isFilled.image(leftCard.additional_image),
                  })}
                  {...(!isMobile && {style: {x: rightCardX}})}
                >
                  <SingleCard
                    card={rightCard}
                    cardIndex={1}
                    isActive={activeDrawerIndex === 1}
                    onOpen={() => setActiveDrawerIndex(1)}
                    onClose={() => setActiveDrawerIndex(null)}
                    bioInfo={slice.primary.drawer_bio_info_right_card}
                    linkResolver={linkResolver}
                    componentIndex={componentIndex}
                    pageUid={pageUid}
                    buttonsData={buttonsData}
                    componentName="stresstimonials-card-right"
                  />
                </RightCardWrapper>
              </div>
            )}
          </div>
        </div>
      </Container>
    </>
  )
}
